/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import { colors, borderRadius, shadows, typography, transitions } from '../../styles/theme';
import { serviceIcons, ServiceKey, ArrowRightIcon } from '../common';

const sectionStyles = css`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const sectionHeaderStyles = css`
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    color: ${colors.text.primary};
    margin-bottom: 0.75rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['3xl']};
    }
  }

  p {
    color: ${colors.text.secondary};
    max-width: 600px;
    margin: 0 auto;
  }
`;

const servicesGridStyles = css`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const serviceCardStyles = css`
  position: relative;
  display: block;
  border-radius: ${borderRadius['2xl']};
  overflow: hidden;
  box-shadow: ${shadows.lg};
  background: white;
  text-decoration: none;
  transition: all ${transitions.normal};

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${shadows['2xl']};
  }

  &:hover .hover-cta {
    opacity: 1;
    transform: translateY(0);
  }

  .poster {
    width: 100%;
    aspect-ratio: 2 / 3;
    object-fit: cover;
    display: block;
  }

  .hover-cta {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 2rem 1rem 1rem;
    background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.75) 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    font-weight: ${typography.fontWeight.semibold};
    font-size: ${typography.fontSize.sm};
    opacity: 0;
    transform: translateY(8px);
    transition: all ${transitions.normal};

    svg {
      width: 16px;
      height: 16px;
    }
  }

  /* Dokunmatik ekranlarda hover olmadığı için bağlantı metni hep görünür */
  @media (hover: none) {
    .hover-cta {
      opacity: 1;
      transform: none;
    }
  }
`;

const fallbackStyles = css`
  width: 100%;
  aspect-ratio: 2 / 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  color: white;

  .icon-wrapper {
    width: 96px;
    height: 96px;
    margin-bottom: 1.25rem;
    border-radius: ${borderRadius.full};
    background: rgba(255, 255, 255, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 46px;
      height: 46px;
    }
  }

  h3 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.xl};
    font-weight: ${typography.fontWeight.bold};
    margin-bottom: 0.5rem;
  }

  span {
    font-size: ${typography.fontSize.sm};
    opacity: 0.85;
  }
`;

interface ServiceItem {
  id: ServiceKey;
  title: string;
  fallbackGradient: string;
  poster: string | null;
  guidePath: string;
}

const servicesData: ServiceItem[] = [
  {
    id: 'cekici',
    title: 'Çekici Hizmeti',
    fallbackGradient: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.primary[700]})`,
    poster: '/images/services/cekici.jpg',
    guidePath: '/nasil-kullanilir/cekici',
  },
  {
    id: 'vinc',
    title: 'Vinç Hizmeti',
    fallbackGradient: `linear-gradient(135deg, ${colors.secondary[500]}, ${colors.secondary[700]})`,
    poster: null,
    guidePath: '/nasil-kullanilir/vinc',
  },
  {
    id: 'nakliye',
    title: 'Nakliye',
    fallbackGradient: `linear-gradient(135deg, #f59e0b, #d97706)`,
    poster: '/images/services/nakliye.jpg',
    guidePath: '/nasil-kullanilir/nakliye',
  },
  {
    id: 'yol-yardim',
    title: 'Yol Yardım',
    fallbackGradient: `linear-gradient(135deg, #ef4444, #b91c1c)`,
    poster: '/images/services/yol-yardim.jpg',
    guidePath: '/nasil-kullanilir/yol-yardim',
  },
  {
    id: 'servis',
    title: 'Servis',
    fallbackGradient: `linear-gradient(135deg, ${colors.accent[500]}, ${colors.accent[700]})`,
    poster: '/images/services/servis.jpg',
    guidePath: '/nasil-kullanilir/servis',
  },
  {
    id: 'vip-tasima',
    title: 'VIP Taşıma',
    fallbackGradient: `linear-gradient(135deg, ${colors.premium[500]}, ${colors.premium[700]})`,
    poster: '/images/services/vip-tasima.jpg',
    guidePath: '/nasil-kullanilir/vip-tasima',
  },
];

export default function ServicesSection() {
  return (
    <section css={sectionStyles}>
      <div css={sectionHeaderStyles}>
        <h2>Hizmetlerimiz</h2>
        <p>Çekici, vinç, yol yardım, nakliye, servis ve VIP taşıma — ihtiyacınız ne olursa olsun tek uygulamada.</p>
      </div>
      <div css={servicesGridStyles}>
        {servicesData.map(service => {
          const Icon = serviceIcons[service.id];
          return (
            <Link key={service.id} to={service.guidePath} css={serviceCardStyles}>
              {service.poster ? (
                <img className="poster" src={service.poster} alt={`${service.title} tanıtımı`} loading="lazy" />
              ) : (
                <div css={fallbackStyles} style={{ background: service.fallbackGradient }}>
                  <div className="icon-wrapper"><Icon /></div>
                  <h3>{service.title}</h3>
                  <span>Detaylı görsel yakında</span>
                </div>
              )}
              <span className="hover-cta">Rehberi İncele <ArrowRightIcon /></span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
